import { useQuery } from '@apollo/client';
import React, { FC } from 'react';
import { useRouter } from 'next/router';
import * as S from './CategoryDesktopFilter.styles';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useServerDataContext } from '@/contexts';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { GetRootCategoriesResponse } from '@/api/types/categories.types';
import { GET_ROOT_CATEGORIES } from '@/api/queries/categories.queries';
import { useParseQuery } from '@/hooks/useParseQuery';
import { PERSISTED_QUERY_PARAMS } from '@/components/Filters/DesktopFilters/CategoryDesktopFilter.constants';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { cleanupSeoUrl } from '@/utils/helpers';
import { hrefWithSearchParams } from '@/components/Filters/FiltersDrawer.helpers';

interface Props {
  isCompact?: boolean;
  isLoading?: boolean;
}

export const CategoryDesktopFilter: FC<Props> = ({
  isLoading,
  isCompact = false,
}) => {
  const {
    currentCategory: serverCurrentCategory,
    rootCategories: serverRootCategories,
  } = useServerDataContext();
  const {
    currentCategory = serverCurrentCategory,
    isAllCategories,
    isSubcategories,
  } = useCurrentCategory();
  const { queryString } = useUrlAndHashFromPath();
  const router = useRouter();

  const {
    keywords,
    categoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
    isShop,
  } = useParseQuery();

  const currentCategoryId = categoryId || currentCategory?.id;

  const isBrowser = typeof window !== 'undefined';

  const { data } = useQuery<GetRootCategoriesResponse>(GET_ROOT_CATEGORIES, {
    variables: {
      keywords,
      filters: {
        categoryId: isAllCategories ? null : currentCategoryId,
        price,
        regionId,
        propertyOptions,
        userLegacyId,
        isShop,
      },
    },
    skip: !isBrowser,
  });

  const categories = data?.categories ?? serverRootCategories?.categories ?? [];

  function getCategoryPathWithQuery(categoryPath: string) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((_value, key) => {
      if (!PERSISTED_QUERY_PARAMS.includes(key)) {
        searchParams.delete(key);
      }
    });
    return hrefWithSearchParams(`/${categoryPath}`, searchParams);
  }

  function handleCategoryClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) {
    event.preventDefault();
    router.push(href);
  }

  const parentCategories = [
    currentCategory?.parent?.parent,
    currentCategory?.parent,
  ];

  const categorySkeletonCount = isCompact ? 7 : 14;

  return (
    <S.Container>
      {parentCategories.map(parentCategory => {
        if (!parentCategory) return null;

        if (isLoading) {
          return (
            <S.ParentCategoryLoader key={parentCategory.id}>
              <SkeletonLoader width={100} height={18} borderRadius={7} />
            </S.ParentCategoryLoader>
          );
        }

        const href = getCategoryPathWithQuery(parentCategory.path);

        return (
          <S.ParentCategory
            key={parentCategory.id}
            dataTestId={SearchResultsPageTestIds.desktopFilters.parentCategory}
            href={cleanupSeoUrl(href)}
            onClick={event => handleCategoryClick(event, href)}
          >
            <AngleBackIcon height={12} width={6} />
            {parentCategory.name}
          </S.ParentCategory>
        );
      })}
      {isLoading ? (
        <S.CurrentCategoryLoader $isCompact={isCompact}>
          <SkeletonLoader width={150} height={18} borderRadius={7} />
        </S.CurrentCategoryLoader>
      ) : (
        <S.CurrentCategory
          $isCompact={isCompact}
          data-testid={SearchResultsPageTestIds.desktopFilters.currentCategory}
        >
          {currentCategory?.name}
        </S.CurrentCategory>
      )}
      {isLoading && !isSubcategories
        ? Array.from({ length: categorySkeletonCount }, (_, index) => (
            <S.CategoryLoader $isCompact={isCompact} key={index}>
              <SkeletonLoader width={150} height={18} borderRadius={7} />
            </S.CategoryLoader>
          ))
        : categories.map(category => {
            const href = getCategoryPathWithQuery(category.path);

            return (
              <S.Category
                $isCompact={isCompact}
                key={category.id}
                href={cleanupSeoUrl(href)}
                onClick={event => handleCategoryClick(event, href)}
                dataTestId={SearchResultsPageTestIds.desktopFilters.category}
              >
                {category.name}
              </S.Category>
            );
          })}
    </S.Container>
  );
};
